import React, { useEffect, useState } from 'react';
import { Loader2, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, LineChart, Line } from 'recharts';
import { Classement } from './Classement';
import { bbFetch, fmtDate, Title, Panel, Scene, SceneNumber, btnOutline } from './bbShared';

const COLORS = { nb_rdv: '#F26A1B', nb_visites_prospects: '#C8372D', nb_visites_clients: '#355070', nb_contacts: '#8A8F98', nb_contrats_prospects: '#2F7D4F', nb_contrats_renouvellements: '#7A4F0E' };
const SHORT = { nb_rdv: 'RDV', nb_visites_prospects: 'Visites prospects', nb_visites_clients: 'Visites clients', nb_contacts: 'Contacts', nb_contrats_prospects: 'Contrats prospects', nb_contrats_renouvellements: 'Contrats renouv.' };

const exportCsv = (data) => {
  const head = ['Commercial', 'Déclarations envoyées', 'Dont à temps', ...data.fields.map(f => f.label)];
  const lines = data.par_commercial.map(r => [r.name, `${r.envoyees}/${r.semaines}`, r.a_temps, ...data.fields.map(f => r[f.key])]);
  const csv = [head, ...lines].map(l => l.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(';')).join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8' }));
  a.download = 'declarations_12_semaines.csv';
  a.click();
};

export const DeclDashboard = ({ token, isAdmin }) => {
  const [data, setData] = useState(null);
  useEffect(() => { bbFetch(token, '/bb/declarations/stats?weeks=12').then(setData).catch(e => toast.error(e.message)); }, [token]);
  if (!data) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#F26A1B]" /></div>;
  const weeks = data.par_semaine.map(w => ({ ...w, label: fmtDate(w.semaine, true).slice(0, 5) }));
  const nbCom = data.par_commercial.length || 1;
  return (
    <div className="space-y-5 bg-[#F3F2EE] -mx-4 sm:-mx-6 px-4 sm:px-6 py-5 rounded-2xl" data-testid="decl-dashboard">
      <div>
        <Title className="text-3xl">Activité déclarée · 12 dernières semaines</Title>
        <p className="text-sm text-slate-500 mt-1">Chiffres issus des déclarations hebdomadaires S-1 envoyées par {isAdmin ? 'les commerciaux' : 'vous'}.</p>
      </div>
      <Scene testId="decl-scene" className="grid grid-cols-2 lg:grid-cols-4 divide-white/10 lg:divide-x">
        {[['nb_rdv', 'RDV'], ['nb_visites_prospects', 'Visites prospects'], ['nb_contrats_prospects', 'Contrats prospects'], ['taux', 'Déclarations à temps']].map(([k, l]) => (
          <div key={k} className="p-5" data-testid={`decl-kpi-${k}`}>
            <p className="text-sm font-bold uppercase tracking-[0.1em] text-white/80">{l}</p>
            <SceneNumber className="mt-2">{k === 'taux' ? `${data.taux_a_temps} %` : data.totaux[k]}</SceneNumber>
            <p className="text-sm text-white/70 mt-1.5">
              {k === 'taux' ? `${data.taux_envoi} % envoyées au total` : k === 'nb_rdv' ? `objectif ${data.objectifs.nb_rdv * 12 * nbCom} sur 12 semaines` : `sur 12 semaines`}
            </p>
          </div>
        ))}
      </Scene>
      <Classement token={token} />
      <div className="grid lg:grid-cols-2 gap-5">
        <Panel testId="decl-chart-weeks">
          <Title className="text-xl mb-3">Évolution par semaine</Title>
          <div className="h-72"><ResponsiveContainer><LineChart data={weeks}><CartesianGrid strokeDasharray="3 3" stroke="#EEECE8" /><XAxis dataKey="label" fontSize={11} /><YAxis fontSize={11} allowDecimals={false} /><Tooltip /><Legend />
            {['nb_rdv', 'nb_visites_prospects', 'nb_visites_clients', 'nb_contrats_prospects', 'nb_contrats_renouvellements'].map(k => <Line key={k} dataKey={k} name={SHORT[k]} stroke={COLORS[k]} strokeWidth={2} dot={false} />)}
          </LineChart></ResponsiveContainer></div>
        </Panel>
        <Panel testId="decl-chart-commercial">
          <Title className="text-xl mb-3">Par commercial</Title>
          <div className="h-72"><ResponsiveContainer><BarChart data={data.par_commercial}><CartesianGrid strokeDasharray="3 3" stroke="#EEECE8" /><XAxis dataKey="name" fontSize={11} /><YAxis fontSize={11} allowDecimals={false} /><Tooltip /><Legend />
            {['nb_rdv', 'nb_visites_prospects', 'nb_visites_clients', 'nb_contrats_prospects', 'nb_contrats_renouvellements'].map(k => <Bar key={k} dataKey={k} name={SHORT[k]} fill={COLORS[k]} />)}
          </BarChart></ResponsiveContainer></div>
        </Panel>
      </div>
      <Panel testId="decl-table">
        <div className="flex items-center justify-between gap-2 mb-3">
          <Title className="text-xl">Détail par commercial</Title>
          <button type="button" className={btnOutline} onClick={() => exportCsv(data)} data-testid="decl-export"><FileSpreadsheet className="w-4 h-4" />Export CSV</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-[11px] uppercase tracking-[0.1em] text-slate-500 text-left align-bottom">
              <th className="pb-2 pr-3">Commercial</th><th className="pb-2 pr-3 text-right">Envoyées</th><th className="pb-2 pr-3 text-right">À temps</th>
              {data.fields.map(f => <th key={f.key} className="pb-2 pr-3 text-right">{SHORT[f.key]}</th>)}
            </tr></thead>
            <tbody>
              {data.par_commercial.map(r => (
                <tr key={r.commercial_id} className="border-t border-[#EEECE8]" data-testid={`decl-row-${r.commercial_id}`}>
                  <td className="py-2.5 pr-3 font-semibold">{r.name}</td>
                  <td className="pr-3 text-right">{r.envoyees} / {r.semaines}</td>
                  <td className="pr-3 text-right">{r.a_temps}</td>
                  {data.fields.map(f => {
                    const o = data.objectifs[f.key] ? data.objectifs[f.key] * r.semaines : null;
                    return <td key={f.key} className={`pr-3 text-right font-bold ${o && r[f.key] < o ? 'text-[#C2510F]' : ''}`}>{r[f.key]}{o ? <span className="text-[10px] font-normal text-slate-400"> / {o}</span> : ''}</td>;
                  })}
                </tr>
              ))}
              <tr className="border-t-2 border-[#25272B] font-bold" data-testid="decl-totaux">
                <td className="py-2.5 pr-3" colSpan={3}>Total</td>
                {data.fields.map(f => <td key={f.key} className="pr-3 text-right">{data.totaux[f.key]}</td>)}
              </tr>
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
};
